import { h, Host } from "@stencil/core";
/** @static base class name */
const baseClass = "ux-dot-pagination";
export class DotPagination {
    constructor() {
        this.total = 0;
        this.page = 1;
        this.maxDots = 7;
        this.withoutArrows = false;
        this.loop = false;
        this.label = "";
        this.ariaLabelNext = "";
        this.ariaLabelPrev = "";
        this.ariaLabelMenuItem = "";
        this.current = this.page - 1;
        this.items = [];
    }
    setTotal() {
        const { total } = this;
        this.items =
            total > 0
                ? Array(total)
                    .fill(1)
                    .map((_item, index) => index)
                : [];
    }
    setPage() {
        this.setCurrent(this.page - 1);
    }
    /** Emitted when a dot or arrow was clicked `<{ page: number; }>` */
    paginationChange;
    setCurrent = (index) => {
        this.current = Math.max(-1, Math.min(this.total - 1, index));
    };
    handleClickDotAt = (index) => {
        if (index === this.current) {
            return;
        }
        const event = this.paginationChange.emit({
            page: index + 1
        });
        if (event.defaultPrevented) {
            return;
        }
        this.setCurrent(index);
    };
    handlePrevPage = () => {
        if (!this.loop && this.current <= 0) {
            return;
        }
        const index = this.current > 0 ? this.current - 1 : this.total - 1;
        this.handleClickDotAt(index);
    };
    handleNextPage = () => {
        if (!this.loop && this.current >= this.total - 1) {
            return;
        }
        const index = this.current < this.total - 1 ? this.current + 1 : 0;
        this.handleClickDotAt(index);
    };
    componentWillLoad() {
        this.setTotal();
    }
    render() {
        const { current, items, total, maxDots, withoutArrows, loop, handleClickDotAt, handlePrevPage, handleNextPage } = this;
        const limit = Math.max(3, maxDots);
        const activeDot = Math.max(0, Math.min(current, total - 1));
        const startIndex = total > limit ? Math.min(total - limit, Math.max(0, Math.round(current - 0.5 * (limit - 1)))) : 0;
        const endIndex = startIndex + Math.min(total, limit);
        return (h(Host, { key: '4a7704dbf4fa16991a67e5c69502ffe7343c6622', class: {
                [baseClass]: true
            } }, h("div", { key: 'caddace8cfd3044f29edf108dcbc46ae7f5f539d', class: `${baseClass}__container`, role: "menu", "aria-label": this.label }, !withoutArrows && (h("ux-icon", { key: 'd3ac0a31497c8534be4b670577b3c1a0338f58a5', name: "chevron-right", weight: "normal", class: {
                [`${baseClass}__arrow`]: true,
                [`${baseClass}__arrow--prev`]: true,
                [`${baseClass}__arrow--disabled`]: !loop && current === 0
            }, onClick: handlePrevPage, tabindex: 0, onKeyPress: (e) => {
                if (e.code === "Enter" || e.code === "Space") {
                    this.handlePrevPage();
                }
            }, role: "button", "aria-disabled": !loop && current === 0 ? "true" : "false", "aria-label": this.ariaLabelNext })), h("div", { key: '6dac17ebb0caee52b3bc0f510cd0a54d1d54a510', class: `${baseClass}__inner` }, items.map((listIndex) => (h("span", { role: "menuitem", "aria-label": `${this.ariaLabelMenuItem} ${listIndex + 1}`, class: {
                [`${baseClass}__item`]: true,
                [`${baseClass}__item--active`]: listIndex === activeDot,
                [`${baseClass}__item--hidden`]: listIndex < startIndex || listIndex >= endIndex,
                [`${baseClass}__item--small`]: (listIndex === startIndex && listIndex > 0) ||
                    (listIndex === endIndex - 1 && listIndex < total - 1)
            }, tabindex: 0, onClick: () => handleClickDotAt(listIndex), onKeyPress: (e) => {
                if (e.code === "Enter" || e.code === "Space") {
                    this.handleClickDotAt(listIndex);
                }
            } }, h("span", { class: `${baseClass}__dot` }))))), !withoutArrows && (h("ux-icon", { key: 'aace71f0e49a76c215e508709a8a1b9e70059afa', name: "chevron-right", weight: "normal", class: {
                [`${baseClass}__arrow`]: true,
                [`${baseClass}__arrow--next`]: true,
                [`${baseClass}__arrow--disabled`]: !loop && current === total - 1
            }, onKeyPress: (e) => {
                if (e.code === "Enter" || e.code === "Space") {
                    this.handleNextPage();
                }
            }, tabindex: 0, "aria-label": this.ariaLabelNext, "aria-disabled": !loop && current === total - 1 ? "true" : "false", role: "button", onClick: handleNextPage })))));
    }
    static get is() { return "ux-dot-pagination"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["dot-pagination.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["dot-pagination.css"]
        };
    }
    static get properties() {
        return {
            "total": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Define total number of pages"
                },
                "attribute": "total",
                "reflect": false,
                "defaultValue": "0"
            },
            "page": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Define current page index"
                },
                "attribute": "page",
                "reflect": false,
                "defaultValue": "1"
            },
            "maxDots": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Define maximum number of visible dots"
                },
                "attribute": "max-dots",
                "reflect": false,
                "defaultValue": "7"
            },
            "withoutArrows": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "false - visible"
                        }],
                    "text": "Define visibility of navigation arrows"
                },
                "attribute": "without-arrows",
                "reflect": false,
                "defaultValue": "false"
            },
            "loop": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "default",
                            "text": "false - visible"
                        }],
                    "text": "Define navigation item loop\nIf clicking an arrow on the last page should jump to the first page and vice versa"
                },
                "attribute": "loop",
                "reflect": false,
                "defaultValue": "false"
            },
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Define container aria-label"
                },
                "attribute": "label",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "ariaLabelNext": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Define next button aria-label"
                },
                "attribute": "aria-label-next",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "ariaLabelPrev": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Define prev button aria-label"
                },
                "attribute": "aria-label-prev",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "ariaLabelMenuItem": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "Sample",
                            "text": "'Menu Item' shows as 'Menu Item 5'"
                        }],
                    "text": "Define dot item label\nWill be extended with dot index`"
                },
                "attribute": "aria-label-menu-item",
                "reflect": false,
                "defaultValue": "\"\""
            }
        };
    }
    static get states() {
        return {
            "current": {},
            "items": {}
        };
    }
    static get events() {
        return [{
                "method": "paginationChange",
                "name": "uxPaginationChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when a dot or arrow was clicked `<{ page: number; }>`"
                },
                "complexType": {
                    "original": "{\n        page: number;\n    }",
                    "resolved": "{ page: number; }",
                    "references": {}
                }
            }];
    }
    static get watchers() {
        return [{
                "propName": "total",
                "methodName": "setTotal"
            }, {
                "propName": "page",
                "methodName": "setPage"
            }];
    }
}
//# sourceMappingURL=dot-pagination.js.map
